import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ICreateCampaignRequest,
	ICreateSocialMediaPostRequest,
	ILocationState,
	TemplateOrTemplateFilter,
} from '../../../models';
import { CampaignType } from '../../../models/AdminModels';
import { BlogPostRequest } from '../../../models/Blogs';
import { useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { getBlogPostAsync } from '../../../queries';
import { IBlogTemplateCard, ITemplateCard } from '../../../viewmodels/AppViewModels';
import { ContentCalendarTemplateBrowser } from './ContentCalendarTemplateBrowser';
import { styleSheet } from './styles';

type Props = {
	templateBrowserCampaignType: CampaignType;
	setTemplateBrowserCampaignType: (campaignType: CampaignType) => void;
	isSuggestionsOn?: boolean;
	/** When provided (e.g. admin), use instead of internal customer routes for opening template composer */
	onCampaignTemplateClicked?: (template: Api.ITemplate, schedule?: Api.IScheduledSend, referrer?: string) => void;
	/** When provided (e.g. admin), use instead of deriving from useUserSession().account */
	campaignTypes?: CampaignType[];
	selectedUserId?: string | null;
	impersonationContext?: Api.IImpersonationContext;
};

const ContentCalendarTemplateBrowserWrapperBase: React.FC<Props> = ({
	templateBrowserCampaignType,
	setTemplateBrowserCampaignType,
	isSuggestionsOn,
	onCampaignTemplateClicked: onCampaignTemplateClickedProp,
	campaignTypes: campaignTypesProp,
	selectedUserId,
	impersonationContext,
}) => {
	const fullscreenModal = useFullscreenModal();
	const userSession = useUserSession();

	const droppableType = 'campaignTemplateCard';

	const isBlogsEnabled = userSession.account?.features?.blogFeature?.enabled;
	const isSocialMediaEnabled = userSession.account?.features?.socialMedia?.enabled;

	const derivedCampaignTypes = React.useMemo<CampaignType[]>(() => {
		const types = [CampaignType.Email];
		if (isSocialMediaEnabled) {
			types.push(CampaignType.Social);
		}
		if (isBlogsEnabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}, [isBlogsEnabled, isSocialMediaEnabled]);

	const campaignTypes = campaignTypesProp ?? derivedCampaignTypes;

	const handleCampaignTemplateClicked = (
		template: Api.ITemplate,
		schedule?: Api.IScheduledSend,
		_referrer = 'contentCalendarAppDrawer'
	) => {
		if (template?.templateType === Api.TemplateType.Email || !template?.templateType) {
			const locationStateEmail: ILocationState<any, ICreateCampaignRequest<TemplateOrTemplateFilter>> = {
				model: {
					onFinish: (_didSend: boolean) => {
						// Email campaign finished
					},
					schedule,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/email/campaigns/create/from-template/${template.id}`,
				state: locationStateEmail,
			});
		} else if (template.templateType === Api.TemplateType.Blog) {
			const locationState: ILocationState<any, BlogPostRequest> = {
				model: {
					schedule,
					template,
				},
			};
			fullscreenModal?.history.push({
				pathname: `/blog/post/create-from-template/${template.id}`,
				state: locationState,
			});
		} else if (template.templateType === Api.TemplateType.SocialMediaPost) {
			const locationStateSocial: ILocationState<any, ICreateSocialMediaPostRequest<Api.ITemplate>> = {
				model: {
					context: template,
					onFinish: (_didSend: boolean) => {
						// Social campaign finished
					},
					schedule,
					sendFromUser: userSession.user,
					type: 'Template',
				},
			};
			fullscreenModal?.history.push({
				pathname: `/social-media/post/create/from-template`,
				state: locationStateSocial,
			});
		}
	};

	const onCampaignTemplateClicked = (template: Api.ITemplate, schedule?: Api.IScheduledSend, referrer?: string) => {
		if (onCampaignTemplateClickedProp) {
			onCampaignTemplateClickedProp(template, schedule, referrer);
		} else {
			handleCampaignTemplateClicked(template, schedule, referrer);
		}
	};

	const launchEditBlogPost = (post: Api.IBlogPost, schedule?: Api.IScheduledSend) => {
		const locationState: ILocationState<any, BlogPostRequest> = {
			model: {
				post,
				schedule,
				sendFromUser: userSession.user,
			},
		};
		const pathname = `/blog/post/edit/${post.id}`;
		fullscreenModal?.history?.push({
			pathname,
			state: locationState,
		});
	};
	const onBlogPostClicked = async (e: React.MouseEvent<HTMLElement>, blog: Api.IBlogReportView | IBlogTemplateCard) => {
		try {
			const result = await getBlogPostAsync({ id: blog.id, userSession });
			launchEditBlogPost(result);
		} catch {
			toast.error('Failed to load blog post');
		}
	};
	const onSocialPostClicked = async (e: React.MouseEvent<HTMLElement>, post: ITemplateCard) => {
		e.preventDefault();
		try {
			const minimalPost: Partial<Api.ISocialMediaPost> = { id: post.id };
			const baseImpersonationContext = impersonationContext?.account?.id ? impersonationContext : undefined;
			const initialImpersonationUserId = post.creator?.id || selectedUserId || baseImpersonationContext?.user?.id;
			const initialImpersonationContext =
				baseImpersonationContext && initialImpersonationUserId
					? {
							...baseImpersonationContext,
							user: { id: initialImpersonationUserId },
						}
					: baseImpersonationContext;

			const postVm = new Api.SocialMediaPostViewModel(userSession, minimalPost);
			if (initialImpersonationContext) {
				postVm.impersonate(initialImpersonationContext);
			}
			await postVm.load();

			const finalImpersonationUserId = postVm.creator?.id || initialImpersonationUserId;
			const finalImpersonationContext =
				baseImpersonationContext && finalImpersonationUserId
					? {
							...baseImpersonationContext,
							user: { id: finalImpersonationUserId },
						}
					: baseImpersonationContext;
			if (finalImpersonationContext) {
				postVm.impersonate(finalImpersonationContext);
			}

			const sendFromUser = postVm.creator || finalImpersonationContext?.user || userSession.user;
			const locationState: ILocationState<Api.SocialMediaPostViewModel, any> = {
				viewModel: postVm,
				model: {
					sendFromUser,
					impersonationContext: finalImpersonationContext,
				},
			};
			const pathname = finalImpersonationContext?.account?.id
				? `/accounts/${finalImpersonationContext.account.id}/content-calendar/post/create`
				: `/social-media/post/edit-post-draft/${postVm.id}`;
			fullscreenModal?.history?.push({
				pathname,
				state: locationState,
			});
		} catch {
			toast.error('Failed to load social post');
		}
	};

	return (
		<div className={css(styleSheet.appDrawerTemplateBrowserContainer)}>
			<ContentCalendarTemplateBrowser
				isAppDrawer={true}
				dragDropProps={{
					droppableId: 'campaignTemplatesList',
					type: droppableType,
					draggingStyles: [styleSheet.campaignTemplateCardDragging],
				}}
				onTemplateTypeSelected={setTemplateBrowserCampaignType}
				onCampaignTemplateClicked={onCampaignTemplateClicked}
				selectedCampaignType={templateBrowserCampaignType}
				campaignTypes={campaignTypes}
				isSuggestionsOn={isSuggestionsOn}
				selectedUserId={selectedUserId}
				forUserId={selectedUserId}
				onBlogPostEditClicked={onBlogPostClicked}
				onSocialPostEditClicked={onSocialPostClicked}
			/>
		</div>
	);
};

export const ContentCalendarTemplateBrowserWrapper = observer(ContentCalendarTemplateBrowserWrapperBase);
